import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  Timestamp,
  query,
  where,
  getDoc,
  increment,
  runTransaction
} from 'firebase/firestore';
import { db } from './firebase';

export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled',
  PENDING: 'pending'
};

// Create a new subscription or extend existing one
export const createSubscription = async (subscriptionData) => {
  try {
    // Check if user has an active subscription
    const activeSubResult = await getUserActiveSubscription(subscriptionData.userId);

    if (activeSubResult.success && activeSubResult.subscription) {
      // User has active subscription - accumulate benefits
      const existingSub = activeSubResult.subscription;
      const now = new Date();
      const existingEndDate = existingSub.endDate.toDate();

      // Calculate new end date: extend from existing end date (or now if already expired)
      const baseDate = existingEndDate > now ? existingEndDate : now;
      const newEndDate = new Date(baseDate);

      const durationDays = typeof subscriptionData.duration === 'number' && !isNaN(subscriptionData.duration)
        ? subscriptionData.duration
        : parseInt(subscriptionData.duration) || 30;

      newEndDate.setDate(newEndDate.getDate() + durationDays);

      // Accumulate job postings, featured jobs, and ads
      const updatedMaxJobPostings = (existingSub.maxJobPostings || 0) + (subscriptionData.maxJobPostings || 0);
      const updatedFeaturedJobs = (existingSub.featuredJobs || 0) + (subscriptionData.featuredJobs || 0);
      const updatedMaxAds = (existingSub.maxAds || 0) + (subscriptionData.maxAds || 0);

      // Update existing subscription with accumulated values
      const updateData = {
        maxJobPostings: updatedMaxJobPostings,
        featuredJobs: updatedFeaturedJobs,
        maxAds: updatedMaxAds,
        endDate: Timestamp.fromDate(newEndDate),
        updatedAt: Timestamp.now(),
        // Keep track of purchase history
        purchaseHistory: [
          ...(existingSub.purchaseHistory || []),
          {
            packageId: subscriptionData.packageId,
            packageName: subscriptionData.packageName,
            price: subscriptionData.price,
            duration: subscriptionData.duration,
            maxJobPostings: subscriptionData.maxJobPostings,
            featuredJobs: subscriptionData.featuredJobs,
            maxAds: subscriptionData.maxAds,
            purchasedAt: Timestamp.now(),
            paymentId: subscriptionData.paymentId,
            razorpayPaymentId: subscriptionData.razorpayPaymentId
          }
        ]
      };

      await updateSubscription(existingSub.id, updateData);

      console.log(`✅ Extended existing subscription: +${subscriptionData.maxJobPostings} jobs, +${subscriptionData.maxAds} ads, +${subscriptionData.duration} days`);
      console.log(`📊 New totals: ${updatedMaxJobPostings} jobs, ${updatedMaxAds} ads, valid until ${newEndDate.toLocaleDateString()}`);

      return {
        success: true,
        id: existingSub.id,
        subscription: { ...existingSub, ...updateData },
        extended: true
      };
    }

    // No active subscription - create new one
    const startDate = Timestamp.now();
    const endDate = new Date();
    // Ensure duration is a valid number, default to 30 days if not
    const durationDays = typeof subscriptionData.duration === 'number' && !isNaN(subscriptionData.duration)
      ? subscriptionData.duration
      : parseInt(subscriptionData.duration) || 30;

    endDate.setDate(endDate.getDate() + durationDays);

    const newSubscription = {
      ...subscriptionData,
      duration: durationDays,
      startDate,
      endDate: Timestamp.fromDate(endDate),
      createdAt: startDate,
      updatedAt: startDate,
      status: SUBSCRIPTION_STATUS.ACTIVE,
      usageStats: {
        applicationsUsed: 0,
        jobPostingsUsed: 0,
        featuredJobsUsed: 0,
        adsUsed: 0,
        resumeDownloads: 0
      },
      purchaseHistory: [
        {
          packageId: subscriptionData.packageId,
          packageName: subscriptionData.packageName,
          price: subscriptionData.price,
          duration: durationDays,
          maxJobPostings: subscriptionData.maxJobPostings || 0,
          featuredJobs: subscriptionData.featuredJobs || 0,
          maxAds: subscriptionData.maxAds || 0,
          purchasedAt: startDate,
          paymentId: subscriptionData.paymentId,
          razorpayPaymentId: subscriptionData.razorpayPaymentId
        }
      ]
    };

    // Remove any undefined fields to prevent Firestore errors
    Object.keys(newSubscription).forEach(key => {
      if (newSubscription[key] === undefined) {
        delete newSubscription[key];
      }
    });

    // Also sanitize purchaseHistory
    newSubscription.purchaseHistory = newSubscription.purchaseHistory.map(entry => {
      const cleanEntry = { ...entry };
      Object.keys(cleanEntry).forEach(key => {
        if (cleanEntry[key] === undefined) {
          delete cleanEntry[key];
        }
      });
      return cleanEntry;
    });

    const docRef = await addDoc(collection(db, 'subscriptions'), newSubscription);
    console.log(`✅ Created new subscription: ${subscriptionData.maxJobPostings} jobs, ${subscriptionData.duration} days`);

    return { success: true, id: docRef.id, subscription: newSubscription, extended: false };
  } catch (error) {
    console.error('Error creating/extending subscription:', error);
    return { success: false, error: error.message };
  }
};

// Get user's active subscription
export const getUserActiveSubscription = async (userId) => {
  try {
    const q = query(
      collection(db, 'subscriptions'),
      where('userId', '==', userId),
      where('status', '==', SUBSCRIPTION_STATUS.ACTIVE)
    );

    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      // Sort in memory to get the most recent
      const sortedDocs = querySnapshot.docs.sort((a, b) => {
        const aTime = a.data().createdAt?.toMillis() || 0;
        const bTime = b.data().createdAt?.toMillis() || 0;
        return bTime - aTime;
      });
      const doc = sortedDocs[0];
      const subscription = {
        id: doc.id,
        ...doc.data()
      };

      // Check if subscription has expired
      const now = new Date();
      const endDate = subscription.endDate.toDate();

      if (now > endDate) {
        // Mark as expired
        await updateSubscription(subscription.id, { status: SUBSCRIPTION_STATUS.EXPIRED });
        return { success: true, subscription: null, expired: true };
      }

      return { success: true, subscription };
    }

    return { success: true, subscription: null };
  } catch (error) {
    console.error('Error fetching user subscription:', error);
    return { success: false, error: error.message };
  }
};

// Get all user subscriptions (history)
export const getUserSubscriptions = async (userId) => {
  try {
    const q = query(
      collection(db, 'subscriptions'),
      where('userId', '==', userId)
    );

    const querySnapshot = await getDocs(q);
    const subscriptions = [];

    querySnapshot.forEach((doc) => {
      subscriptions.push({
        id: doc.id,
        ...doc.data()
      });
    });

    // Sort in memory by createdAt (newest first)
    subscriptions.sort((a, b) => {
      const aTime = a.createdAt?.toMillis() || 0;
      const bTime = b.createdAt?.toMillis() || 0;
      return bTime - aTime;
    });

    return { success: true, subscriptions };
  } catch (error) {
    console.error('Error fetching user subscriptions:', error);
    return { success: false, error: error.message };
  }
};

// Update subscription
export const updateSubscription = async (subscriptionId, updateData) => {
  try {
    const subscriptionRef = doc(db, 'subscriptions', subscriptionId);
    await updateDoc(subscriptionRef, {
      ...updateData,
      updatedAt: Timestamp.now()
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating subscription:', error);
    return { success: false, error: error.message };
  }
};

// Cancel subscription
export const cancelSubscription = async (subscriptionId) => {
  try {
    const subscriptionRef = doc(db, 'subscriptions', subscriptionId);
    await updateDoc(subscriptionRef, {
      status: SUBSCRIPTION_STATUS.CANCELLED,
      cancelledAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });

    return { success: true };
  } catch (error) {
    console.error('Error cancelling subscription:', error);
    return { success: false, error: error.message };
  }
};

// Update usage stats with atomic increment
export const updateUsageStats = async (subscriptionId, usageType, incrementValue = 1) => {
  try {
    console.log(`📊 [updateUsageStats] Updating ${usageType} for subscription ${subscriptionId} by ${incrementValue}`);

    const subscriptionRef = doc(db, 'subscriptions', subscriptionId);
    const subscriptionDoc = await getDoc(subscriptionRef);

    if (!subscriptionDoc.exists()) {
      console.error('❌ [updateUsageStats] Subscription not found:', subscriptionId);
      return { success: false, error: 'Subscription not found' };
    }

    // Use atomic increment to prevent race conditions
    const updateData = {
      [`usageStats.${usageType}`]: increment(incrementValue),
      updatedAt: Timestamp.now()
    };

    await updateDoc(subscriptionRef, updateData);

    // Get updated stats
    const updatedDoc = await getDoc(subscriptionRef);
    const newStats = updatedDoc.data().usageStats || {};

    console.log(`✅ [updateUsageStats] Updated successfully. New stats:`, newStats);
    return { success: true, newStats };
  } catch (error) {
    console.error('❌ [updateUsageStats] Error updating usage stats:', error);
    return { success: false, error: error.message };
  }
};

// Decrement usage stats (for refunds or cancellations)
export const decrementUsageStats = async (subscriptionId, usageType, decrementValue = 1) => {
  try {
    console.log(`📉 [decrementUsageStats] Decrementing ${usageType} for subscription ${subscriptionId} by ${decrementValue}`);

    const subscriptionRef = doc(db, 'subscriptions', subscriptionId);
    const subscriptionDoc = await getDoc(subscriptionRef);

    if (!subscriptionDoc.exists()) {
      return { success: false, error: 'Subscription not found' };
    }

    // Use atomic increment with negative value
    const updateData = {
      [`usageStats.${usageType}`]: increment(-decrementValue),
      updatedAt: Timestamp.now()
    };

    await updateDoc(subscriptionRef, updateData);

    // Get updated stats
    const updatedDoc = await getDoc(subscriptionRef);
    const newStats = updatedDoc.data().usageStats || {};

    console.log(`✅ [decrementUsageStats] Decremented successfully. New stats:`, newStats);
    return { success: true, newStats };
  } catch (error) {
    console.error('❌ [decrementUsageStats] Error:', error);
    return { success: false, error: error.message };
  }
};

// Check if user can perform action based on package limits
export const checkPackageLimit = async (userId, actionType) => {
  try {
    console.log(`🔍 [checkPackageLimit] Checking ${actionType} limit for user:`, userId);

    const result = await getUserActiveSubscription(userId);

    if (!result.success) {
      console.error('❌ [checkPackageLimit] Failed to get subscription:', result.error);
      return { success: false, error: result.error };
    }

    if (!result.subscription) {
      console.warn('⚠️ [checkPackageLimit] No active subscription found');
      return {
        success: false,
        error: 'No active subscription. Please purchase a package to continue.',
        needsSubscription: true
      };
    }

    const subscription = result.subscription;
    const usageStats = subscription.usageStats || {
      applicationsUsed: 0,
      jobPostingsUsed: 0,
      featuredJobsUsed: 0,
      resumeDownloads: 0
    };

    console.log('📊 [checkPackageLimit] Current usage stats:', usageStats);
    console.log('📦 [checkPackageLimit] Package limits:', {
      maxApplications: subscription.maxApplications,
      maxJobPostings: subscription.maxJobPostings,
      featuredJobs: subscription.featuredJobs
    });

    // Check limits based on action type
    let limitInfo = {};

    switch (actionType) {
      case 'application':
        const maxApps = subscription.maxApplications || 0;
        const usedApps = usageStats.applicationsUsed || 0;

        if (maxApps > 0 && usedApps >= maxApps) {
          console.warn(`⚠️ [checkPackageLimit] Application limit reached: ${usedApps}/${maxApps}`);
          return {
            success: false,
            error: 'Application limit reached',
            limitReached: true,
            current: usedApps,
            max: maxApps,
            remaining: 0
          };
        }

        limitInfo = {
          current: usedApps,
          max: maxApps,
          remaining: maxApps - usedApps
        };
        break;

      case 'jobPosting':
        const maxJobs = subscription.maxJobPostings || 0;
        const usedJobs = usageStats.jobPostingsUsed || 0;

        if (maxJobs > 0 && usedJobs >= maxJobs) {
          console.warn(`⚠️ [checkPackageLimit] Job posting limit reached: ${usedJobs}/${maxJobs}`);
          return {
            success: false,
            error: 'Job posting limit reached',
            limitReached: true,
            current: usedJobs,
            max: maxJobs,
            remaining: 0
          };
        }

        limitInfo = {
          current: usedJobs,
          max: maxJobs,
          remaining: maxJobs - usedJobs
        };
        break;

      case 'featuredJob':
        const maxFeatured = subscription.featuredJobs || 0;
        const usedFeatured = usageStats.featuredJobsUsed || 0;

        if (maxFeatured > 0 && usedFeatured >= maxFeatured) {
          console.warn(`⚠️ [checkPackageLimit] Featured job limit reached: ${usedFeatured}/${maxFeatured}`);
          return {
            success: false,
            error: 'Featured job limit reached',
            limitReached: true,
            current: usedFeatured,
            max: maxFeatured,
            remaining: 0
          };
        }

        limitInfo = {
          current: usedFeatured,
          max: maxFeatured,
          remaining: maxFeatured - usedFeatured
        };
        break;

      case 'adPosting':
        const maxAds = subscription.maxAds || 0;
        const usedAds = usageStats.adsUsed || 0;

        if (maxAds > 0 && usedAds >= maxAds) {
          console.warn(`⚠️ [checkPackageLimit] Ad posting limit reached: ${usedAds}/${maxAds}`);
          return {
            success: false,
            error: 'Ad posting limit reached. Please purchase a marketing package.',
            limitReached: true,
            current: usedAds,
            max: maxAds,
            remaining: 0
          };
        }

        limitInfo = {
          current: usedAds,
          max: maxAds,
          remaining: maxAds - usedAds
        };
        break;

      default:
        console.warn('⚠️ [checkPackageLimit] Unknown action type:', actionType);
        break;
    }

    console.log(`✅ [checkPackageLimit] Limit check passed for ${actionType}:`, limitInfo);
    return {
      success: true,
      subscription,
      canProceed: true,
      ...limitInfo
    };
  } catch (error) {
    console.error('❌ [checkPackageLimit] Error checking package limit:', error);
    return { success: false, error: error.message };
  }
};

// Get subscription analytics for admin
export const getSubscriptionAnalytics = async () => {
  try {
    const subscriptionsSnapshot = await getDocs(collection(db, 'subscriptions'));

    const analytics = {
      totalSubscriptions: subscriptionsSnapshot.size,
      activeSubscriptions: 0,
      expiredSubscriptions: 0,
      cancelledSubscriptions: 0,
      revenue: 0,
      packageBreakdown: {},
      userTypeBreakdown: {}
    };

    subscriptionsSnapshot.forEach((doc) => {
      const subscription = doc.data();

      // Count by status
      switch (subscription.status) {
        case SUBSCRIPTION_STATUS.ACTIVE:
          analytics.activeSubscriptions++;
          break;
        case SUBSCRIPTION_STATUS.EXPIRED:
          analytics.expiredSubscriptions++;
          break;
        case SUBSCRIPTION_STATUS.CANCELLED:
          analytics.cancelledSubscriptions++;
          break;
      }

      // Calculate revenue
      analytics.revenue += subscription.price || 0;

      // Package breakdown
      const packageName = subscription.packageName || 'Unknown';
      analytics.packageBreakdown[packageName] = (analytics.packageBreakdown[packageName] || 0) + 1;

      // User type breakdown
      const userType = subscription.userType || 'Unknown';
      analytics.userTypeBreakdown[userType] = (analytics.userTypeBreakdown[userType] || 0) + 1;
    });

    return { success: true, analytics };
  } catch (error) {
    console.error('Error fetching subscription analytics:', error);
    return { success: false, error: error.message };
  }
};

// Renew subscription
export const renewSubscription = async (userId, packageData) => {
  try {
    // Cancel current active subscription
    const currentResult = await getUserActiveSubscription(userId);
    if (currentResult.success && currentResult.subscription) {
      await cancelSubscription(currentResult.subscription.id);
    }

    // Create new subscription
    const newSubscriptionData = {
      userId,
      packageId: packageData.id,
      packageName: packageData.name,
      packageType: packageData.type,
      price: packageData.price,
      duration: packageData.duration,
      features: packageData.features,
      maxApplications: packageData.maxApplications,
      maxJobPostings: packageData.maxJobPostings,
      featuredJobs: packageData.featuredJobs,
      resumeTemplates: packageData.resumeTemplates,
      profileBoost: packageData.profileBoost,
      candidateSearch: packageData.candidateSearch,
      analyticsAccess: packageData.analyticsAccess,
      userType: packageData.type
    };

    return await createSubscription(newSubscriptionData);
  } catch (error) {
    console.error('Error renewing subscription:', error);
    return { success: false, error: error.message };
  }
};

// Check if user has ever used a free package
export const hasUsedFreePackage = async (userId) => {
  try {
    const result = await getUserSubscriptions(userId);

    if (!result.success) {
      console.error('Error checking free package usage:', result.error);
      // Fail safe: assume false to not block legitimate users if fetch fails
      // But maybe better to assume true? Let's return error
      return { success: false, error: result.error };
    }

    // Check if any subscription has price === 0
    // Also check purchaseHistory within each subscription
    const hasFree = result.subscriptions.some(sub => {
      // Check main subscription object
      if (sub.price === 0) return true;

      const history = sub.purchaseHistory || [];
      return history.some(item => item.price === 0);
    });

    return { success: true, hasUsedFree: hasFree };
  } catch (error) {
    console.error('Error checking free package usage:', error);
    return { success: false, error: error.message };
  }
};
